import { DomainError } from './errors'
import { BUSINESS_TIME_ZONE, parseLocalDate, parseLocalTime, Temporal } from './time'
import type { FineStatus } from './vocabulary'

/**
 * CMP-2 (S6, decision 4): a traffic fine notified to the company. The notice
 * is typed as printed; the rental that had the car is found by plate and
 * time; the renter's data goes to the authority before the deadline the
 * configuration sets; the admin fee is charged from the register, at the
 * amount the rental was handed over with, and collected at the counter or by
 * a payment link, never charged to a card by itself.
 */

const NEXT: Readonly<Record<FineStatus, readonly FineStatus[]>> = {
  registered: ['matched', 'not_ours', 'withdrawn'],
  // Unmatched again while nothing was sent and no fee charged: a wrong pick among several.
  matched: ['registered', 'communicated', 'withdrawn'],
  communicated: ['closed', 'withdrawn'],
  // A notice marked "not ours" by mistake is reopened, not typed twice.
  not_ours: ['registered'],
  closed: [],
  withdrawn: [],
}

export function fineTransitions(from: FineStatus): readonly FineStatus[] {
  return NEXT[from]
}

export function canMoveFine(from: FineStatus, to: FineStatus): boolean {
  return NEXT[from].includes(to)
}

export function assertFineMove(from: FineStatus, to: FineStatus): void {
  if (!canMoveFine(from, to)) {
    throw new DomainError('fine_status', `a ${from} fine cannot become ${to}`, {
      from,
      to,
      allowed: NEXT[from],
    })
  }
}

/** A fine named to its rental: where its admin fee may be charged. */
export const FINE_FEE_STATUSES = [
  'matched',
  'communicated',
] as const satisfies readonly FineStatus[]

/** Still to act on (RPT-3's open fines): not yet closed, ruled out or withdrawn. */
export const OPEN_FINE_STATUSES = [
  'registered',
  'matched',
  'communicated',
] as const satisfies readonly FineStatus[]

/** The renter's data not sent yet: the deadline runs. */
export const FINE_DEADLINE_STATUSES = [
  'registered',
  'matched',
] as const satisfies readonly FineStatus[]

/**
 * A plate as the register keys it: capitals and digits only. Notices print
 * "AB 123 CD" or "AB123CD"; a vehicle's plate may carry hyphens (the
 * configuration allows them), which the comparison drops as well.
 */
export function plateKey(text: string): string {
  return text.toUpperCase().replace(/[\s.-]/g, '')
}

const PLATE_KEY = /^[A-Z0-9]{2,15}$/

export function isPlateKey(key: string): boolean {
  return PLATE_KEY.test(key)
}

/** A notice number as printed, with its spacing made regular: the register's key, with the authority. */
export function noticeNumberKey(text: string): string {
  return text.trim().replace(/\s+/g, ' ').toUpperCase()
}

/**
 * The instants a Rome wall-clock minute, as a notice prints it, may be: one
 * on most days; two in the hour the clocks go back, which happens twice (both
 * are candidates: the notice cannot say which); none in the hour the clocks
 * go forward, which does not exist (refused, never moved).
 */
export function violationInstants(date: string, time: string): Date[] {
  const wall = parseLocalDate(date).toPlainDateTime(parseLocalTime(time))
  const found = new Map<number, Date>()
  for (const disambiguation of ['earlier', 'later'] as const) {
    const zoned = wall.toZonedDateTime(BUSINESS_TIME_ZONE, { disambiguation })
    // Only a reading that gives the same wall clock back is the time printed.
    if (Temporal.PlainDateTime.compare(zoned.toPlainDateTime(), wall) === 0) {
      found.set(zoned.epochMilliseconds, new Date(zoned.epochMilliseconds))
    }
  }
  if (found.size === 0) {
    throw new DomainError(
      'invalid_time',
      'that time does not exist in Rome: the clocks went forward',
      {
        date,
        time,
      },
    )
  }
  return [...found.values()].sort((a, b) => a.getTime() - b.getTime())
}

/** A rental's hold of its car: from the hand-over to the return recorded (open while out). */
export interface Possession {
  rentalId: string
  start: Date
  end: Date | null
}

/** A notice prints the minute: the violation is somewhere in [t, t + 60 s). */
const MINUTE_MS = 60_000

/**
 * Whether the rental had the car during the violation's minute: the
 * hand-over counts (start inclusive), the return does not (end exclusive).
 */
export function possessedDuring(possession: Possession, instant: Date): boolean {
  const from = instant.getTime()
  return (
    possession.start.getTime() < from + MINUTE_MS &&
    (possession.end === null || from < possession.end.getTime())
  )
}

export type FineMatch =
  | { kind: 'none' }
  | { kind: 'one'; rentalId: string }
  | { kind: 'several'; rentalIds: string[] }

/**
 * The rentals of the plate's car that had it at the violation's minute, at
 * either instant of a repeated hour. Only a single candidate is matched by
 * itself; with several, the Owner picks.
 */
export function matchFine(
  instants: readonly Date[],
  possessions: readonly Possession[],
): FineMatch {
  const ids = [
    ...new Set(
      possessions
        .filter((possession) => instants.some((instant) => possessedDuring(possession, instant)))
        .map((possession) => possession.rentalId),
    ),
  ].sort()
  const [only] = ids
  if (ids.length === 0 || only === undefined) return { kind: 'none' }
  if (ids.length === 1) return { kind: 'one', rentalId: only }
  return { kind: 'several', rentalIds: ids }
}

/** The days of `fines.communication_days`: the configuration's, checked again here. */
export function assertCommunicationDays(days: unknown): asserts days is number {
  if (typeof days !== 'number' || !Number.isInteger(days) || days < 1 || days > 365) {
    throw new DomainError(
      'fines_not_configured',
      'fines.communication_days is not set: the deadline cannot be counted',
      {},
    )
  }
}

/**
 * The last day to send the renter's data: counted from the day the notice
 * reached the company, never from the violation (art. 126-bis C.d.S.: 60
 * days, in the configuration).
 */
export function fineDeadline(notifiedOn: string, days: number): string {
  assertCommunicationDays(days)
  return parseLocalDate(notifiedOn).add({ days }).toString()
}

/** How near the deadline is told: within this many days, every morning. */
export const FINE_DUE_SOON_DAYS = 7

/** Days left until the deadline, from a Rome calendar day: 0 on the day itself, negative once past. */
export function daysUntilDeadline(deadlineOn: string, today: string): number {
  return parseLocalDate(today).until(parseLocalDate(deadlineOn), { largestUnit: 'days' }).days
}

export type DeadlineState = 'overdue' | 'due_soon' | 'running'

export function deadlineState(deadlineOn: string, today: string): DeadlineState {
  const left = daysUntilDeadline(deadlineOn, today)
  if (left < 0) return 'overdue'
  return left <= FINE_DUE_SOON_DAYS ? 'due_soon' : 'running'
}
